import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { MENUS } from "../lib/menus";
import { ROLE_LABEL, waktu } from "../lib/constants";
import { api, fileUrl } from "../lib/api";

function NavGroup({ item, pathname }) {
  const active = item.children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  return (
    <div>
      <button className={`nav-item w-full justify-between ${active ? "text-white" : ""}`} onClick={() => setOpen(!open)} data-testid={`nav-group-${item.label.toLowerCase()}`}>
        <span className="flex items-center gap-3"><item.icon size={18} />{item.label}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-4 mt-1 space-y-0.5 border-l border-emerald-700/60 pl-3">
          {item.children.map((c) => <NavLink key={c.to} to={c.to} data-testid={`nav-${c.to.slice(1)}`} className={({ isActive }) => `nav-item py-2 text-[13px] ${isActive ? "nav-item-active" : ""}`}>{c.label}</NavLink>)}
        </div>
      )}
    </div>
  );
}

function Sidebar({ open, setOpen }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const menu = MENUS[user.role] || [];
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside data-testid="sidebar" className={`fixed top-0 bottom-0 left-0 z-40 w-64 flex flex-col text-white transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} style={{ background: "linear-gradient(180deg,#064e3b 0%,#065f46 100%)" }}>
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <img src="/logo.webp" alt="logo" className="h-11 w-11 rounded-full bg-white object-contain p-0.5" />
          <div>
            <div className="font-extrabold text-[15px] leading-tight">Portal Pendampingan</div>
            <div className="text-[11px] text-emerald-200">Yayasan Bintang Pelita Nusa</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {menu.map((m) => m.children ? <NavGroup key={m.label} item={m} pathname={pathname} /> : (
            <NavLink key={m.to} to={m.to} onClick={() => setOpen(false)} data-testid={`nav-${m.to.slice(1)}`} className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}><m.icon size={18} />{m.label}</NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="w-full btn bg-rose-500 hover:bg-rose-600 text-white"><LogOut size={16} /> Logout</button>
        </div>
      </aside>
    </>
  );
}

function Notifications() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const load = () => api.get("/notifikasi").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const openIt = async () => { setOpen(!open); if (!open && data.unread) { await api.post("/notifikasi/read-all"); load(); } };
  return (
    <div className="relative">
      <button data-testid="notif-bell-btn" onClick={openIt} className="relative p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors">
        <Bell size={20} />
        {data.unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{data.unread}</span>}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 card shadow-xl z-50 page-enter">
          <div className="px-4 py-3 border-b border-slate-100 font-bold text-sm">Notifikasi</div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {data.items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Tidak ada notifikasi</div>}
            {data.items.map((n) => (
              <button key={n.id} onClick={() => { setOpen(false); if (n.link) nav(n.link); }} className={`w-full text-left px-4 py-3 hover:bg-emerald-50/60 ${!n.read ? "bg-emerald-50/40" : ""}`}>
                <div className="text-sm font-semibold text-slate-800">{n.judul}</div>
                <div className="text-xs text-slate-600 line-clamp-2">{n.isi}</div>
                <div className="text-[10px] text-slate-400 mt-1">{waktu(n.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <div className="min-h-screen">
      <Sidebar open={open} setOpen={setOpen} />
      <div className="lg:ml-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 px-4 sm:px-8 flex items-center justify-between no-print">
          <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu size={22} /></button>
          <div className="hidden lg:block text-sm text-slate-500">inklusi.portalpena.co.id</div>
          <div className="flex items-center gap-3">
            <Notifications />
            <div className="text-right hidden sm:block">
              <div data-testid="topbar-user-name" className="text-sm font-bold text-slate-900 leading-tight">{user.nama}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{ROLE_LABEL[user.role]}</div>
            </div>
            {user.foto ? <img src={fileUrl(user.foto)} alt="" className="h-10 w-10 rounded-full object-cover border" /> : <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center">{user.nama?.charAt(0)}</div>}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-7xl w-full mx-auto page-enter" key={pathname}><Outlet /></main>
      </div>
    </div>
  );
}
